// Group quiz client helpers.
//
// Was a thin wrapper around firebase-js writes + onSnapshot listeners.
// Now talks to /api/group/* routes that read/write D1 directly. The
// votely_uid cookie middleware identifies the caller; no Firebase Auth.
//
// The four listenTo* functions used to be Firestore onSnapshot
// subscriptions; here they poll /api/group/[id]/state every 3 seconds
// (a small enough window that the lobby/reveal pages feel live without
// hammering the worker). The unsubscribe shape matches what the
// existing callers expect so the call-site refactor is a no-op.

import {
  Group,
  GroupMember,
  GroupMemberAnswers,
  GroupPrediction,
  CreateGroupInput,
  CreateGroupResult,
  MemberCompletion,
} from '../../types/groups';
import { debugError, debugLog } from '../debug-logger';

const POLL_INTERVAL_MS = 3000;

export type Unsubscribe = () => void;

function buildInviteUrl(groupId: string): string {
  // Invites land on the canonical group URL, which renders the compare
  // invite landing for non-members (compare redesign, phase 5). The old
  // /join route still works for any links already in the wild.
  if (typeof window === 'undefined') return `/group/${groupId}`;
  return `${window.location.origin}/group/${groupId}`;
}

// Normalize a JSON response that came back from /api/group/[id]/state
// or /api/group/[id]. The API returns timestamps as unix ms integers;
// the old Firestore-backed shape returned Timestamp objects with a
// toMillis() method. We need a shim so the existing isGroupExpired etc.
// helpers keep working without per-call-site rewrites.
type ApiGroup = {
  groupId: string;
  name: string | null;
  completedCount: number;
  memberCount: number;
  revealNotifiedAt: number | null;
  retakeNotifiedAt: number | null;
  retakeOf?: string | null;
  questionSet: unknown;
  createdAt: number;
  expiresAt: number | null;
  hostUserId?: string | null;
  you?: string | null;
  isHost?: boolean;
  status?: string | null;
  mode?: string | null;
  maxParticipants?: number | null;
  minQuizType?: string | null;
  creatorQuizType?: string | null;
};

function tsShim(ms: number | null) {
  if (ms === null) return undefined;
  return {
    toMillis: () => ms,
  };
}

function fromApiGroup(g: ApiGroup): Group {
  return {
    groupId: g.groupId,
    name: g.name ?? undefined,
    completedCount: g.completedCount,
    memberCount: g.memberCount,
    revealNotifiedAt: tsShim(g.revealNotifiedAt),
    retakeNotifiedAt: tsShim(g.retakeNotifiedAt),
    retakeOf: g.retakeOf ?? undefined,
    questionIds: (g.questionSet as string[] | null) ?? [],
    locale: 'en',
    hostUserId: g.hostUserId ?? '',
    you: g.you ?? null,
    isHost: g.isHost ?? false,
    status: (g.status as Group['status']) ?? 'open',
    mode: (g.mode as Group['mode']) ?? 'group',
    maxParticipants: g.maxParticipants ?? null,
    minQuizType: (g.minQuizType as Group['minQuizType']) ?? 'short',
    creatorQuizType: (g.creatorQuizType as Group['creatorQuizType']) ?? undefined,
    createdAt: tsShim(g.createdAt),
    expiresAt: tsShim(g.expiresAt),
  } as unknown as Group;
}

type ApiMember = {
  userId: string;
  displayName: string | null;
  status: string;
  joinedAt: number;
  completedAt: number | null;
};

function fromApiMember(m: ApiMember): GroupMember {
  return {
    userId: m.userId,
    displayName: m.displayName ?? '',
    status: m.status as GroupMember['status'],
    joinedAt: tsShim(m.joinedAt),
    completedAt: tsShim(m.completedAt),
  } as unknown as GroupMember;
}

type ApiAnswer = {
  userId: string;
  answers: number[] | null;
  cubePosition: { x: number; y: number; z: number } | null;
  archetype: string | null;
  personalResultId: string | null;
  completedAt: number;
};

function fromApiAnswer(a: ApiAnswer): GroupMemberAnswers {
  return {
    userId: a.userId,
    answers: a.answers ?? [],
    cubePosition: a.cubePosition ?? { x: 0, y: 0, z: 0 },
    archetype: a.archetype ?? '',
    personalResultId: a.personalResultId ?? undefined,
    completedAt: tsShim(a.completedAt),
  } as unknown as GroupMemberAnswers;
}

type ApiPrediction = {
  userId: string;
  macroCellIndex: number | null;
  archetypeGuess: string | null;
  governanceGuess: number | null;
  createdAt: number;
};

function fromApiPrediction(p: ApiPrediction): GroupPrediction {
  return {
    userId: p.userId,
    macroCellIndex: p.macroCellIndex ?? 0,
    archetypeGuess: p.archetypeGuess ?? '',
    governanceGuess: p.governanceGuess ?? undefined,
    createdAt: tsShim(p.createdAt),
  } as unknown as GroupPrediction;
}

export class GroupCreationError extends Error {
  constructor(public readonly status: number, public readonly reason: string | null) {
    super('Group creation failed');
    this.name = 'GroupCreationError';
  }
}

export async function createGroup(input: CreateGroupInput): Promise<CreateGroupResult> {
  const r = await fetch('/api/group', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: input.name,
      hostDisplayName: input.hostDisplayName,
      questionIds: input.questionIds,
      locale: input.locale,
      retakeOf: input.retakeOf,
      mode: input.mode,
      maxParticipants: input.maxParticipants,
      creatorPersonalResultId: input.creatorPersonalResultId,
      creatorQuizType: input.creatorQuizType,
      attribution: input.attribution,
    }),
  });
  if (!r.ok) {
    const body = await r.json().catch(() => null) as { reason?: unknown } | null;
    debugError('createGroup failed', r.status);
    throw new GroupCreationError(r.status, typeof body?.reason === 'string' ? body.reason : null);
  }
  const data = (await r.json()) as CreateGroupResult;
  debugLog('createGroup ok, id:', data.groupId);
  return { groupId: data.groupId, inviteUrl: data.inviteUrl || buildInviteUrl(data.groupId), attribution: data.attribution };
}

export async function getGroup(groupId: string): Promise<Group | null> {
  try {
    const r = await fetch(`/api/group/${groupId}`, { credentials: 'same-origin' });
    if (r.status === 404) return null;
    if (!r.ok) return null;
    return fromApiGroup((await r.json()) as ApiGroup);
  } catch (err) {
    debugError('getGroup failed:', err);
    return null;
  }
}

// Polls /api/group/[id]/state and invokes cb with just the group slice.
export function listenToGroup(
  groupId: string,
  cb: (group: Group | null) => void,
): Unsubscribe {
  return startStatePolling(groupId, (state) => {
    cb(state ? state.group : null);
  });
}

export function listenToMembers(
  groupId: string,
  cb: (members: GroupMember[]) => void,
): Unsubscribe {
  return startStatePolling(groupId, (state) => {
    cb(state ? state.members : []);
  });
}

export function listenToAnswers(
  groupId: string,
  cb: (answers: GroupMemberAnswers[]) => void,
): Unsubscribe {
  return startStatePolling(groupId, (state) => {
    cb(state ? state.answers : []);
  });
}

export function listenToPredictions(
  groupId: string,
  cb: (predictions: GroupPrediction[]) => void,
): Unsubscribe {
  return startStatePolling(groupId, (state) => {
    cb(state ? state.predictions : []);
  });
}

// Single polled listener for the third-party embed widget. Hits the
// unauthenticated /embed-state endpoint: the cookie-gated /state can never
// work inside a cross-site iframe (SameSite=Lax cookie is neither sent nor
// settable there), which left every external embed stuck on "Loading...".
// The endpoint returns pseudonymous member ids, which flow through the
// normal normalizers unchanged.
export function listenToEmbedState(
  groupId: string,
  cb: (
    state: { group: Group; members: GroupMember[]; answers: GroupMemberAnswers[] } | null,
  ) => void,
): Unsubscribe {
  let stopped = false;
  const poll = async () => {
    try {
      const r = await fetch(`/api/group/${groupId}/embed-state`);
      if (stopped) return;
      if (!r.ok) {
        cb(null);
        return;
      }
      const raw = (await r.json()) as {
        group: ApiGroup;
        members: ApiMember[];
        answers: ApiAnswer[];
      };
      cb({
        group: fromApiGroup(raw.group),
        members: raw.members.map(fromApiMember),
        answers: raw.answers.map(fromApiAnswer),
      });
    } catch (err) {
      debugError('embed-state poll failed:', err);
      if (!stopped) cb(null);
    }
  };
  void poll();
  const timer = setInterval(poll, 3000);
  return () => {
    stopped = true;
    clearInterval(timer);
  };
}

// Each listenTo* sets up its own poll. That means 4 listeners on the
// same reveal page fire 4 parallel polls every 3s, which is wasteful.
// Mitigation: a per-groupId in-memory cache that coalesces. The first
// listener in a group starts the poll; subsequent listeners reuse the
// cached snapshot and the same timer. When the last listener
// unsubscribes the timer stops.
type SnapshotState = {
  group: Group;
  members: GroupMember[];
  answers: GroupMemberAnswers[];
  predictions: GroupPrediction[];
};

type PollEntry = {
  callbacks: Set<(state: SnapshotState | null) => void>;
  timer: ReturnType<typeof setInterval> | null;
  last: SnapshotState | null;
};

const POLL_REGISTRY = new Map<string, PollEntry>();

function startStatePolling(
  groupId: string,
  cb: (state: SnapshotState | null) => void,
): Unsubscribe {
  let entry = POLL_REGISTRY.get(groupId);
  if (!entry) {
    entry = { callbacks: new Set(), timer: null, last: null };
    POLL_REGISTRY.set(groupId, entry);
  }
  entry.callbacks.add(cb);

  // First subscriber: kick off the poll loop. Also fire one immediate
  // fetch so callers don't wait the full POLL_INTERVAL_MS for the
  // first frame.
  if (entry.timer === null) {
    const tick = async () => {
      const e = POLL_REGISTRY.get(groupId);
      if (!e) return;
      try {
        const r = await fetch(`/api/group/${groupId}/state`, {
          credentials: 'same-origin',
        });
        if (!r.ok) {
          e.callbacks.forEach((fn) => fn(null));
          return;
        }
        const raw = await r.json();
        const snap: SnapshotState = {
          group: fromApiGroup(raw.group as ApiGroup),
          members: (raw.members as ApiMember[]).map(fromApiMember),
          answers: (raw.answers as ApiAnswer[]).map(fromApiAnswer),
          predictions: (raw.predictions as ApiPrediction[]).map(fromApiPrediction),
        };
        e.last = snap;
        e.callbacks.forEach((fn) => fn(snap));
      } catch (err) {
        debugError('group state poll failed', err);
      }
    };
    void tick();
    entry.timer = setInterval(tick, POLL_INTERVAL_MS);
  } else if (entry.last !== null) {
    // New subscriber on an existing poll: fire the cached value
    // synchronously so the caller doesn't see undefined.
    cb(entry.last);
  }

  return () => {
    const e = POLL_REGISTRY.get(groupId);
    if (!e) return;
    e.callbacks.delete(cb);
    if (e.callbacks.size === 0 && e.timer !== null) {
      clearInterval(e.timer);
      POLL_REGISTRY.delete(groupId);
    }
  };
}

export async function joinGroup(groupId: string, displayName: string): Promise<void> {
  const r = await fetch(`/api/group/${groupId}/join`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ displayName }),
  });
  if (!r.ok) {
    let reason = '';
    try {
      reason = ((await r.json()) as { reason?: string })?.reason ?? '';
    } catch {
      // non-JSON error body; fall through with an empty reason
    }
    debugError('joinGroup failed', r.status, reason);
    const e = new Error('join failed') as Error & { reason?: string };
    e.reason = reason;
    throw e;
  }
}

export class GroupContributionError extends Error {
  constructor(public readonly status: number, public readonly reason: string | null) {
    super('Group contribution failed');
    this.name = 'GroupContributionError';
  }
}

export async function contributeAnswers(
  groupId: string,
  completion: MemberCompletion,
): Promise<void> {
  const r = await fetch(`/api/group/${groupId}/answers`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      answers: completion.answers,
      cubePosition: completion.cubePosition,
      archetype: completion.archetype,
      personalResultId: completion.personalResultId,
    }),
  });
  if (!r.ok) {
    const body = await r.json().catch(() => null) as { reason?: unknown } | null;
    debugError('contributeAnswers failed', r.status);
    throw new GroupContributionError(r.status, typeof body?.reason === 'string' ? body.reason : null);
  }
}

export async function getCompletedAnswers(groupId: string): Promise<GroupMemberAnswers[]> {
  try {
    const r = await fetch(`/api/group/${groupId}/state`, { credentials: 'same-origin' });
    if (!r.ok) return [];
    const raw = await r.json();
    return (raw.answers as ApiAnswer[]).map(fromApiAnswer);
  } catch (err) {
    debugError('getCompletedAnswers failed:', err);
    return [];
  }
}

export async function getMembers(groupId: string): Promise<GroupMember[]> {
  try {
    const r = await fetch(`/api/group/${groupId}/state`, { credentials: 'same-origin' });
    if (!r.ok) return [];
    const raw = await r.json();
    return (raw.members as ApiMember[]).map(fromApiMember);
  } catch (err) {
    debugError('getMembers failed:', err);
    return [];
  }
}

export async function savePrediction(
  groupId: string,
  macroCellIndex: number,
  archetypeGuess: string,
  governanceGuess?: number,
): Promise<void> {
  const r = await fetch(`/api/group/${groupId}/predictions`, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ macroCellIndex, archetypeGuess, governanceGuess }),
  });
  if (!r.ok) {
    debugError('savePrediction failed', r.status);
    throw new Error('save failed');
  }
}

export async function getMyPrediction(groupId: string): Promise<GroupPrediction | null> {
  // Pull from state and filter to the caller's own prediction. /state now
  // returns pseudonymous userIds plus `group.you` (this caller's pseudonym),
  // so we match on that instead of the raw uid - no /api/auth/me round-trip
  // needed, and no raw uid ever leaves the server.
  try {
    const stateR = await fetch(`/api/group/${groupId}/state`, { credentials: 'same-origin' });
    if (!stateR.ok) return null;
    const raw = await stateR.json();
    const you = (raw.group as ApiGroup)?.you;
    if (!you) return null;
    const mine = (raw.predictions as ApiPrediction[]).find((p) => p.userId === you);
    return mine ? fromApiPrediction(mine) : null;
  } catch (err) {
    debugError('getMyPrediction failed:', err);
    return null;
  }
}

export async function saveDebateFeedback(
  groupId: string,
  text: string,
  context?: {
    groupName?: string | null;
    archetype?: string | null;
    memberCount?: number;
    completedCount?: number;
  },
): Promise<void> {
  const trimmed = text.trim().slice(0, 2000);
  if (!trimmed) throw new Error('empty');
  const r = await fetch(`/api/group/${groupId}/debate-feedback`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text: trimmed, context }),
  });
  if (!r.ok) {
    debugError('saveDebateFeedback failed', r.status);
    throw new Error('save failed');
  }
}

export async function leaveGroup(groupId: string): Promise<void> {
  const r = await fetch(`/api/group/${groupId}/membership`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  if (!r.ok) {
    debugError('leaveGroup failed', r.status);
    throw new Error('leave failed');
  }
}

export function getInviteUrl(groupId: string): string {
  return buildInviteUrl(groupId);
}

export function isGroupExpired(group: Group): boolean {
  if (!group.expiresAt) return false;
  const ms = group.expiresAt.toMillis ? group.expiresAt.toMillis() : 0;
  return ms > 0 && ms < Date.now();
}

export function isGroupAcceptingJoins(group: Group): boolean {
  if (group.status !== 'open') return false;
  if (isGroupExpired(group)) return false;
  return true;
}

export async function closeGroup(groupId: string): Promise<void> {
  const r = await fetch(`/api/group/${groupId}/close`, {
    method: 'POST',
    credentials: 'same-origin',
  });
  if (!r.ok) {
    debugError('closeGroup failed', r.status);
    throw new Error('close failed');
  }
}

export async function saveGroupNotificationInterest(input: {
  groupId: string;
  email: string;
  source: 'host_create' | 'member_join';
}): Promise<void> {
  try {
    const r = await fetch('/api/group-notifications', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        groupId: input.groupId,
        email: input.email.toLowerCase().trim(),
        source: input.source,
      }),
    });
    if (!r.ok) {
      debugError('saveGroupNotificationInterest failed', r.status);
    }
  } catch (err) {
    debugError('saveGroupNotificationInterest threw', err);
  }
}
